import classNames from "classnames/bind";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Checkbox } from "../../Checkbox";
import { DefaultPageSizeOptions } from "../../Pagination/Pagination.constants";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import { DataRowActions } from "../DataRowActions";
import type {
  DataCellProps,
  DataRowProps,
  HeaderType,
  ItemBaseType,
} from "../types";
import DataCell from "./DataCell/DataCell";
import DateCell from "./DataCell/DateCell";
import MoneyCell from "./DataCell/MoneyCell";
import NumberCell from "./DataCell/NumberCell";
import TextCell from "./DataCell/TextCell";
import { getNextAvailableRowIdx, getRowNumber } from "./DataRow.helpers";
import styles from "./DataRow.module.scss";

const cx = classNames.bind(styles);

export const DataRow = <ItemType extends ItemBaseType>({
  item,
  leafHeaders,
  rowIndex,
  isLoading,
  isBusy,
  showRowNumbers,
  rowNumberDirection = "forward",
  getRowAdornment,
  getRowIndicatorStyle,
  onChangeSelection,
  onRowClick,
  items,
  getItemActions,
  isSelectionInverted,
  isSelectionEnabled,
  isSingleSelection,
  selected,
  hideSelectionCheckboxes,
  getItemId,
  density,
  intent,
  tableRowActionsRightPosition,
  tableRowActionsMaxWidth,
  setTableRowActionsMaxWidth,
  shouldLeafCellsBreakMaxWidthCeiling,
  pagination,
}: DataRowProps<ItemType>) => {
  const tableRowActionsRef = useRef<HTMLTableCellElement>(null);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [areRowActionsActive, setAreRowActionsActive] =
    useState<boolean>(false);

  const ref = useHotkeys(
    ["shift+up", "shift+down", "ctrl+a"],
    (_, hotkeys) => {
      if (!isSelectionEnabled || !item) return;

      switch (hotkeys.hotkey) {
        case "shift+up":
          onChangeSelection?.(
            items[
              getNextAvailableRowIdx<ItemType>(
                "reverse",
                rowIndex,
                items,
                selected ?? {},
                pagination
                  ? {
                      floor:
                        pagination.page * pagination.pageSize -
                        pagination.pageSize,
                      ceiling: pagination.page * pagination.pageSize - 1,
                    }
                  : undefined
              )
            ],
            {
              isAlreadySelected: false,
            }
          );
          break;
        case "shift+down":
          {
            onChangeSelection?.(
              items[
                getNextAvailableRowIdx<ItemType>(
                  "forward",
                  rowIndex,
                  items,
                  selected ?? {},
                  pagination
                    ? {
                        floor:
                          pagination.page * pagination.pageSize -
                          pagination.pageSize,
                        ceiling: pagination.page * pagination.pageSize - 1,
                      }
                    : undefined
                )
              ],
              {
                isAlreadySelected: false,
              }
            );
          }
          break;
        case "ctrl+a":
          onChangeSelection?.(item, { selectAll: true });
          break;
      }
    },
    {
      scopes: ["rows"],
    },
    [isSelectionEnabled, isSingleSelection, selected, items, pagination]
  );

  const handleMouseEnter: React.MouseEventHandler<HTMLTableRowElement> = () =>
    setIsHovered(true);
  const handleMouseLeave: React.MouseEventHandler<HTMLTableRowElement> = () => {
    if (areRowActionsActive) return;
    setIsHovered(false);
  };
  const handleRowSelectCellClick: React.MouseEventHandler<
    HTMLTableCellElement
  > = (event) => {
    event.stopPropagation();

    if (isLoading || !item) return;

    onRowClick?.(item);
  };

  useEffect(() => {
    if (
      !tableRowActionsRef.current?.clientWidth ||
      tableRowActionsMaxWidth > tableRowActionsRef.current?.clientWidth ||
      tableRowActionsMaxWidth === tableRowActionsRef.current.clientWidth + 8
    )
      return;

    setTableRowActionsMaxWidth(tableRowActionsRef.current.clientWidth + 8);
  }, [tableRowActionsRef.current?.clientWidth, tableRowActionsMaxWidth]);

  const rowAdornmentConfiguration = useMemo(() => {
    if (!getRowAdornment || !item) return null;

    return getRowAdornment({ idx: rowIndex, item });
  }, [rowIndex, item, getRowAdornment]);
  const actions = useMemo(() => {
    if (item && getItemActions) {
      const newActions = getItemActions({ item });

      if (newActions?.length) {
        return newActions;
      } else return;
    } else return;
  }, [item, getItemActions]);
  const isRowSelected = useMemo(
    () =>
      Boolean(
        !isLoading &&
          item &&
          ((getItemId && selected?.[getItemId(item)]) || isSelectionInverted)
      ),
    [isLoading, item, getItemId, selected, isSelectionInverted]
  );
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isActuallyHovered = isHovered || areRowActionsActive;

  const rowIndicator = (
    <div className={cx("selection-cell-wrapper")}>
      {showRowNumbers && !isLoading ? (
        <span className={cx("row-number")}>
          {getRowNumber(rowNumberDirection, rowIndex, {
            page: pagination?.page ?? 1,
            pageSize: pagination?.pageSize ?? DefaultPageSizeOptions[0],
            total: pagination?.total,
            pageTotal: items?.length ?? 0,
          })}
        </span>
      ) : null}
      <Skeleton isLoading={isLoading}>
        {!hideSelectionCheckboxes && isSelectionEnabled ? (
          <Checkbox
            name="datatable-is-row-selected-checkbox"
            value={Boolean(isRowSelected)}
            isDisabled={isLoading}
            onChange={() =>
              item &&
              onChangeSelection?.(item, {
                isAlreadySelected: Boolean(isRowSelected),
              })
            }
          />
        ) : null}
      </Skeleton>
    </div>
  );

  return (
    <tr
      ref={ref}
      tabIndex={0}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cx(
        "dataRow",
        "vertical",
        {
          selected: isRowSelected,
          hovered: isActuallyHovered,
          "has-row-adornment": rowAdornmentConfiguration,
        },
        rowAdornmentConfiguration?.colorVariant &&
          `row-adornment-color--${rowAdornmentConfiguration.colorVariant}`
      )}
      aria-selected={isRowSelected}
      onClick={(event) => {
        if (isLoading || !item || showRowNumbers || isSelectionEnabled) return;
        if (
          (event.target as HTMLElement).nodeName !== "TD" ||
          (event?.target as HTMLTableRowElement)?.getAttribute(
            "data-is-row-action-button"
          )
        )
          return;

        onRowClick?.(item);
        if (isSingleSelection || isSelectionEnabled)
          onChangeSelection?.(item, { isAlreadySelected: isRowSelected });
      }}
    >
      {(!hideSelectionCheckboxes && isSelectionEnabled) || showRowNumbers ? (
        <td
          className={cx(
            "cell",
            `cell--${density}`,
            "isSelection",
            "sticky",
            "sticky--default",
            leafHeaders[0] && !leafHeaders[0].isFixed && "sticky--last",
            "sticky--content",
            onRowClick && "clickable"
          )}
          data-role="data-table-row-indicator"
          tabIndex={0}
          onClick={handleRowSelectCellClick}
          style={item ? getRowIndicatorStyle?.({ idx: rowIndex, item }) : {}}
        >
          {rowAdornmentConfiguration ? (
            <Tooltip content={rowAdornmentConfiguration?.text ?? ""}>
              {rowIndicator}
            </Tooltip>
          ) : (
            rowIndicator
          )}
        </td>
      ) : null}

      {leafHeaders.map((leafHeader, leafHeaderIdx) => (
        <DataRowCell<ItemType>
          key={`${leafHeaderIdx}-${leafHeader.key}`}
          rowIndex={rowIndex}
          columnIdx={leafHeaderIdx}
          column={leafHeader}
          leafHeaders={leafHeaders}
          items={items}
          item={item}
          hasRowActions={!!getItemActions}
          isHovered={isActuallyHovered}
          isLoading={isLoading}
          isBusy={isBusy}
          isSelected={isRowSelected}
          density={density}
          intent={intent}
          tableRowActionsMaxWidth={tableRowActionsMaxWidth}
          shouldLeafCellsBreakMaxWidthCeiling={
            shouldLeafCellsBreakMaxWidthCeiling
          }
        />
      ))}

      {getItemActions ? (
        <td
          ref={tableRowActionsRef}
          className={cx(
            "vertical",
            "cell--floating-right",
            !isLoading && isActuallyHovered && "visible"
          )}
          style={{
            ...(tableRowActionsRightPosition !== null
              ? { right: tableRowActionsRightPosition }
              : {}),
            backgroundColor: "transparent",
          }}
          data-row-actions
        >
          {actions?.length ? (
            <DataRowActions
              actions={actions}
              density={density}
              handleOpen={setAreRowActionsActive}
            />
          ) : null}
        </td>
      ) : null}
    </tr>
  );
};

const DataRowCell = <ItemType extends ItemBaseType>(
  props: {
    column: HeaderType<ItemType>;
    columnIdx: number;
    isHovered: boolean;
    hasRowActions: boolean;
  } & Omit<DataCellProps<ItemType>, "dataKey"> &
    Pick<
      DataRowProps<ItemType>,
      | "tableRowActionsMaxWidth"
      | "shouldLeafCellsBreakMaxWidthCeiling"
      | "leafHeaders"
    >
) => {
  const {
    columnIdx,
    item,
    leafHeaders,
    rowIndex,
    isHovered,
    isSelected,
    isLoading,
    isBusy,
    items,
    density,
    intent,
    tableRowActionsMaxWidth,
    shouldLeafCellsBreakMaxWidthCeiling,
    hasRowActions,
  } = props;
  const {
    key: dataKey,
    isFixed,
    renderComponent,
    getStyle,
    alignment,
  } = props.column;

  const styleMemoized = useMemo<CSSProperties>(() => {
    return {
      ...(hasRowActions && columnIdx === leafHeaders.length - 1
        ? {
            paddingRight: tableRowActionsMaxWidth,
          }
        : {}),
    };
  }, [columnIdx, leafHeaders, hasRowActions, tableRowActionsMaxWidth]);

  return (
    <DataCell
      rowIndex={rowIndex}
      dataKey={dataKey}
      item={item}
      items={items}
      leafHeaders={leafHeaders}
      renderComponent={renderComponent}
      getStyle={getStyle}
      isFixed={isFixed}
      isLastFixedCell={
        isFixed &&
        leafHeaders[columnIdx + 1] &&
        !leafHeaders[columnIdx + 1].isFixed
      }
      shouldLeafCellsBreakMaxWidthCeiling={shouldLeafCellsBreakMaxWidthCeiling}
      isHovered={isHovered}
      isLoading={isLoading}
      isBusy={isBusy}
      isSelected={isSelected}
      alignment={alignment}
      density={density}
      intent={intent}
      style={styleMemoized}
      className={cx(
        columnIdx === leafHeaders.length - 1 && "cell--last",
        isFixed && "sticky--content"
      )}
    />
  );
};

DataRow.Checkbox = Checkbox;
DataRow.DateCell = DateCell;
DataRow.MoneyCell = MoneyCell;
DataRow.NumberCell = NumberCell;
DataRow.TextCell = TextCell;
